import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LadyBirdDeedFlorida = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Attorney Florida — All 67 Counties | Spanski Law</title>
        <meta name="description" content="Florida Lady Bird Deed attorney serving all 67 counties. Avoid probate, protect your home, keep full control. $800-$1,000 flat fee. Call 941-206-2223." />
        <link rel="canonical" href="https://thelawyerlady.com/lady-bird-deed-florida" />
        <meta property="og:title" content="Lady Bird Deed Attorney Florida — All 67 Counties | Spanski Law" />
        <meta property="og:description" content="Florida Lady Bird Deed attorney serving all 67 counties. Avoid probate, protect your home, keep full control. $800-$1,000 flat fee." />
        <meta property="og:url" content="https://thelawyerlady.com/lady-bird-deed-florida" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://thelawyerlady.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Lady Bird Deed Florida",
              "item": "https://thelawyerlady.com/lady-bird-deed-florida"
            }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Attorney",
          "name": "Season A. Spanski",
          "alternateName": "Spanski Law, PLLC",
          "url": "https://thelawyerlady.com",
          "telephone": "+1-941-206-2223",
          "email": "contact@thelawyerlady.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Venice",
            "addressRegion": "FL",
            "addressCountry": "US"
          },
          "areaServed": {
            "@type": "State",
            "name": "Florida"
          },
          "priceRange": "$800-$1000",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
              "opens": "09:00",
              "closes": "17:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Friday",
              "opens": "09:00",
              "closes": "12:00"
            }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          "name": "Spanski Law, PLLC",
          "url": "https://thelawyerlady.com",
          "telephone": "+1-941-206-2223",
          "email": "contact@thelawyerlady.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Venice",
            "addressRegion": "FL",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "27.0998",
            "longitude": "-82.4543"
          },
          "areaServed": {
            "@type": "State",
            "name": "Florida"
          },
          "priceRange": "$800-$1000",
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
              "opens": "09:00",
              "closes": "17:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Friday",
              "opens": "09:00",
              "closes": "12:00"
            }
          ],
          "serviceType": ["Lady Bird Deed", "Enhanced Life Estate Deed", "Estate Planning", "Probate Avoidance"]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Lady Bird Deed Florida</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#faf9f7] mb-6">
            Lady Bird Deed Attorney Florida — Serving All 67 Counties
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 mb-8 leading-relaxed">
            I'm Season Spanski, and I help Florida homeowners protect their property and avoid probate using Lady Bird Deeds. Whether you're in Miami, Jacksonville, Tampa, or anywhere in between, I can prepare your Lady Bird Deed remotely — no office visit required.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-12">
            <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
              <Phone className="w-5 h-5" />
              Call 941-206-2223
            </a>
            <Link to="/#contact" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
              Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex flex-wrap gap-6 text-sm text-[#faf9f7]/60">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#c9a87c]" />
              <span>Venice, FL (Serving All Florida)</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#c9a87c]" />
              <span>Mon-Thu 9am-5pm, Fri 9am-12pm</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose prose-lg prose-invert max-w-none">
            
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6">What Is a Lady Bird Deed?</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              A Lady Bird Deed — also called an enhanced life estate deed — is a legal document that lets you name who will inherit your Florida home when you pass away, while keeping complete control of the property during your lifetime. You can sell it, refinance it, rent it out, or change your mind entirely. Your beneficiaries have no say until after you're gone.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              The real power of a Lady Bird Deed is what happens at death: the property transfers automatically to your named beneficiaries without going through probate. No court proceedings, no attorney fees for probate administration, no delays. Your family simply records your death certificate and the property is theirs.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Why Florida Homeowners Use Lady Bird Deeds</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Florida is one of only a handful of states that recognizes Lady Bird Deeds. Homeowners here use them because they solve a real problem: how do you pass your home to your children without putting them through probate or giving up control while you're still alive?
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 my-8">
              {[
                "Avoid probate completely",
                "Keep full control of your property",
                "Preserve Florida homestead exemption",
                "No gift tax consequences",
                "Revocable at any time",
                "Medicaid compliant when done correctly",
                "Beneficiaries get stepped-up tax basis",
                "Simple transfer at death"
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-[#141414] rounded-lg border border-[#c9a87c]/10">
                  <CheckCircle className="w-5 h-5 text-[#c9a87c] flex-shrink-0 mt-0.5" />
                  <span className="text-[#faf9f7]/80">{benefit}</span>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Who Qualifies for a Lady Bird Deed in Florida?</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              If you own real property in Florida and want it to pass to specific people when you die — without going through probate — you're likely a good candidate for a Lady Bird Deed. Most of my clients are:
            </p>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li>Homeowners who want to leave their house to their children</li>
              <li>Parents who want to avoid putting their family through probate</li>
              <li>Property owners who don't want the complexity of a trust</li>
              <li>People doing Medicaid planning who need to protect their home</li>
              <li>Anyone who wants a simple, affordable estate planning solution for real estate</li>
            </ul>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              A Lady Bird Deed works for single-family homes, condos, townhouses, vacant land, and investment properties. If you own it in Florida, we can likely use a Lady Bird Deed to transfer it.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">How I Prepare Your Lady Bird Deed</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              My process is straightforward. We start with a phone call or video consultation where I learn about your property, your goals, and who you want to inherit. I'll ask about your title, any mortgage, and whether the property is your homestead.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Once I have what I need, I draft your Lady Bird Deed using Florida-specific language that protects your rights. You'll review the draft, and when you're ready, we arrange for signing and notarization. If you're local to Venice or Sarasota, we can meet in person. If you're anywhere else in Florida, we handle everything remotely using online notarization.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              After signing, I record the deed with your county's clerk of court. The whole process typically takes 1-2 weeks from start to finish.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Cost of a Lady Bird Deed in Florida</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I charge a flat fee of $800-$1,000 for a straightforward Lady Bird Deed. This includes the consultation, deed preparation, review, and guidance through signing. Recording fees paid to the county are additional — typically $30-50 depending on the county.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              More complex situations — multiple properties, unusual title issues, or properties held in a trust or LLC — may cost more. I'll always tell you the total before we start.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Serving All 67 Florida Counties</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              My office is in Venice, but I serve homeowners throughout Florida. Thanks to remote online notarization, you don't need to travel to get a Lady Bird Deed done right. I regularly work with clients in <Link to="/lady-bird-deed-sarasota" className="text-[#c9a87c] hover:underline">Sarasota</Link>, <Link to="/lady-bird-deed-tampa-fl" className="text-[#c9a87c] hover:underline">Tampa</Link>, <Link to="/lady-bird-deed-orlando-fl" className="text-[#c9a87c] hover:underline">Orlando</Link>, <Link to="/lady-bird-deed-fort-lauderdale" className="text-[#c9a87c] hover:underline">Fort Lauderdale</Link>, Miami, Jacksonville, and everywhere in between.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Related Resources</h2>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li><Link to="/blog/what-is-a-lady-bird-deed-florida" className="text-[#c9a87c] hover:underline">What Is a Lady Bird Deed in Florida? The Complete Guide</Link></li>
              <li><Link to="/blog/lady-bird-deed-vs-living-trust-florida" className="text-[#c9a87c] hover:underline">Lady Bird Deed vs Living Trust — Which Do You Need?</Link></li>
              <li><Link to="/blog/how-much-does-a-lady-bird-deed-cost-florida" className="text-[#c9a87c] hover:underline">How Much Does a Lady Bird Deed Cost in Florida?</Link></li>
              <li><Link to="/blog/does-lady-bird-deed-avoid-probate-florida" className="text-[#c9a87c] hover:underline">Does a Lady Bird Deed Avoid Probate?</Link></li>
            </ul>

          </div>
        </article>

        {/* CTA Section */}
        <section id="contact-section" className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-4">Ready to Protect Your Florida Home?</h2>
            <p className="text-[#faf9f7]/70 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation to find out if a Lady Bird Deed is right for you. I'll answer your questions and explain exactly how the process works.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
                <Phone className="w-5 h-5" />
                Call 941-206-2223
              </a>
              <Link to="/#contact" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
                Schedule Online
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LadyBirdDeedFlorida;
